import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, of, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AccountType } from '../enum/account-type.enum';

interface AccountTypeResponse {
  user_type: number;
  access_pages: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AccountTypeService {
  private readonly superAdminPages = ['0', '1', '2', '3', '4', '5', '6', '7'];

  /* ===============================
        STATE
  =============================== */

  private _accountType$ = new BehaviorSubject<AccountType | null>(null);
  accountType$ = this._accountType$.asObservable();

  private _accessPages$ = new BehaviorSubject<string[]>([]);
  accessPages$ = this._accessPages$.asObservable();

  constructor(private http: HttpClient) {
    this.loadFromStorage();
  }

  /* ===============================
        API
  =============================== */

  getAccountType() {
    const cached = this.getCachedAccountType();

    if (cached) {
      this.applyAccountType(cached);
      return of(cached);
    }

    return this.http
      .get<any>(
        `${environment.Server_URL}/get-type-user`
      )
      .pipe(
        map((res) => this.normalizeAccountType(res)),
        catchError(() => {
          const fallback = this.getCachedAccountType();

          return fallback ? of(fallback) : of({ user_type: 0, access_pages: [] });
        }),
        tap((res) => this.applyAccountType(res))
      );
  }

  normalizeAccountType(res: any): AccountTypeResponse {
    const rawType =
      res?.user_type ??
      res?.type ??
      res?.user?.type ??
      localStorage.getItem('user_type');

    const userType = Number(rawType) || 0;
    const pages = this.normalizeAccessPages(
      res?.access_pages ??
      res?.accessPages ??
      res?.user?.access_pages ??
      localStorage.getItem('access_pages'),
      userType
    );

    return {
      user_type: userType,
      access_pages: pages
    };
  }

  /* ===============================
        STORAGE
  =============================== */

  private loadFromStorage() {
    const storedType = localStorage.getItem('user_type');
    const storedPages = localStorage.getItem('access_pages');

    if (storedType) {
      this._accountType$.next(Number(storedType) as AccountType);
    }

    if (storedPages) {
      this._accessPages$.next(this.normalizeAccessPages(storedPages, Number(storedType)));
    }
  }

  private getCachedAccountType(): AccountTypeResponse | null {
    const storedType = localStorage.getItem('user_type');

    if (!storedType) {
      return null;
    }

    return this.normalizeAccountType({
      user_type: storedType,
      access_pages: localStorage.getItem('access_pages')
    });
  }

  setAccountContext(userType: number, accessPages: string[]): void {
    this.applyAccountType({
      user_type: userType,
      access_pages: this.normalizeAccessPages(accessPages, userType)
    });
  }

  private applyAccountType(res: AccountTypeResponse): void {
    const type = res.user_type as AccountType;
    const pages = res.access_pages;

    this._accountType$.next(type);
    this._accessPages$.next(pages);

    localStorage.setItem('user_type', res.user_type.toString());
    localStorage.setItem('access_pages', JSON.stringify(pages));
  }

  private normalizeAccessPages(rawPages: any, userType: number): string[] {
    if (userType === AccountType.SuperAdmin) {
      return this.superAdminPages;
    }

    let pages = rawPages;

    if (typeof pages === 'string') {
      try {
        pages = JSON.parse(pages);
      } catch {
        pages = pages
          .split(',')
          .map((page: string) => page.trim())
          .filter(Boolean);
      }
    }

    if (!Array.isArray(pages)) {
      return [];
    }

    return pages.map((page) => String(page));
  }

  clearStorage() {
    localStorage.removeItem('user_type');
    localStorage.removeItem('access_pages');
    this._accountType$.next(null);
    this._accessPages$.next([]);
  }

  /* ===============================
        ACCESS CHECK
  =============================== */

  hasAccess(pageId: string): boolean {
    return this.isSuperAdmin() || this._accessPages$.value.includes(String(pageId));
  }

  get currentAccessPages(): string[] {
    return this._accessPages$.value;
  }

  /* ===============================
        ROLE HELPERS
  =============================== */

  get currentAccountType(): AccountType | null {
    return this._accountType$.value;
  }

  setAccountType(type: AccountType) {
    this._accountType$.next(type);
  }

  isAdmin(): boolean {
    return this.currentAccountType === AccountType.Admin;
  }

  isSuperAdmin(): boolean {
    return this.currentAccountType === AccountType.SuperAdmin;
  }

  isSubAdmin(): boolean {
    return this.currentAccountType === AccountType.SubAdmin;
  }

  isEmployee(): boolean {
    return this.currentAccountType === AccountType.Employee;
  }

  isDeliveryHero(): boolean {
    return this.currentAccountType === AccountType.DeliveryHero;
  }

  isClient(): boolean {
    return this.currentAccountType === AccountType.Client;
  }

  isAffiliation(): boolean {
    return this.currentAccountType === AccountType.Affiliation;
  }
  isA(): boolean {
    return this.currentAccountType === AccountType.PageA;
  }
  isB(): boolean {
    return this.currentAccountType === AccountType.PageB;
  }
  isC(): boolean {
    return this.currentAccountType === AccountType.PageC;
  }
  /* ===============================
        HIGH LEVEL PERMISSIONS
  =============================== */

  canManageUsers(): boolean {
    return this.isAdmin() || this.isSuperAdmin();
  }

  canAccessFinancials(): boolean {
    return this.isAdmin() || this.isSuperAdmin() || this.isSubAdmin();
  }

  canAccessPOS(): boolean {
    return this.currentAccountType !== AccountType.Client;
  }

}
